import type { Recommendation } from '@shared/dream-components/src';
import { type ReactNode, useEffect, useMemo, useState } from 'react';

import useEmail from '~/hooks/useEmail';
import useRecommendations from '~/hooks/useLoaders/useRecommendations';
import useGlobalModals from '../GlobalModals/hooks/useGlobalModals';

type RenderProps = {
  email?: string;
  isOpen: boolean;
  isLoading: boolean;
  recommendations: Recommendation[];
  submittedIds: string[];
  onSubmitSuccess: (recommendations: Recommendation[]) => void;
};

type Props = {
  onOpenChange: (value: boolean) => void;
  children: (props: RenderProps) => ReactNode;
};

const DESIRED_RECOMMENDATIONS_COUNT = 6;

const RecommendationsModalContext = ({ onOpenChange, children }: Props) => {
  const [email] = useEmail();
  const { isLoading, recommendations, boostedRecommendations } = useRecommendations();
  const [submittedIds, setSubmittedIds] = useState<string[]>([]);
  const { currentModal } = useGlobalModals();
  const isOpen = currentModal === 'recommendations';

  // Grab the correct number of recommendations to display (top 4 + boosts up to 6 total)
  // then transform them to the correct structure for the modal
  const visibleRecommendations = useMemo(() => {
    const safeRecommendations = Array.isArray(recommendations) ? recommendations : [];
    const safeBoosted = Array.isArray(boostedRecommendations) ? boostedRecommendations : [];

    return safeRecommendations
      .slice(0, 4)
      .reverse()
      .concat([...safeBoosted].reverse())
      .slice(0, DESIRED_RECOMMENDATIONS_COUNT)
      .reverse()
      .map((rec) => ({
        id: rec.id,
        name: rec.publication?.name ?? '',
        description: rec.publication?.description ?? '',
        logo_url: rec.publication?.thumbnail_image_url ?? '',
        site_url: rec.publication?.site_url ?? '',
        sponsored: rec.boosted ?? false,
        recommended_publication_id: rec.recommended_publication_id ?? null,
        isOwner: false,
      }));
  }, [recommendations, boostedRecommendations]);

  const onSubmitSuccess = (recommendations: Recommendation[]) => {
    setSubmittedIds((prev) => [...prev, ...recommendations.map((r) => r.id)]);
  };

  useEffect(() => {
    const noRecs = visibleRecommendations.length === 0;
    const subscribedToAll = new Set(submittedIds).size === visibleRecommendations.length;

    if (isOpen && !isLoading && (noRecs || subscribedToAll)) onOpenChange(false);
  });

  return children({
    email,
    isOpen,
    isLoading,
    recommendations: visibleRecommendations,
    submittedIds,
    onSubmitSuccess,
  });
};

export default RecommendationsModalContext;
